import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Plus, Trash2, MailOpen, Pencil, X, Eye, FileDown, KeyRound, UploadCloud } from "lucide-react";
import Logo from "@/components/Logo";
import RichTextEditor from "@/components/RichTextEditor";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const emptyJob = { title: "", department: "", location: "", employment_type: "", summary: "", description: "", active: true };
const emptyInsight = { title: "", category: "Regulatory Delivery", summary: "", content: "", image_url: "", published: false };
const categories = ["Regulatory Delivery", "Assurance", "Governance", "FATF & AML", "PMO", "Ways of Working", "General"];
const inputCls = "w-full rounded-lg border border-[#0B1F3A]/15 px-4 py-2.5 text-sm outline-none focus:border-[#A87B1F] focus:ring-2 focus:ring-[#A87B1F]/15";

export default function AdminDashboard() {
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [insights, setInsights] = useState(null);
  const [applications, setApplications] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyJob);
  const [editingInsight, setEditingInsight] = useState(null);
  const [insightForm, setInsightForm] = useState(emptyInsight);
  const [busy, setBusy] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const featuredRef = useRef(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [e, j, a, apps] = await Promise.all([
        api.get("/enquiries"), api.get("/jobs/all"), api.get("/insights/all"), api.get("/applications"),
      ]);
      setEnquiries(e.data);
      setJobs(j.data);
      setInsights(a.data);
      setApplications(apps.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("ic_admin_token");
        navigate("/admin/login");
      } else {
        toast.error(formatApiError(err));
        setEnquiries([]); setJobs([]); setInsights([]); setApplications([]);
      }
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("ic_admin_token")) { navigate("/admin/login"); return; }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => { localStorage.removeItem("ic_admin_token"); navigate("/admin/login"); };

  const markRead = async (id) => {
    await api.patch(`/enquiries/${id}/read`);
    setEnquiries((es) => es.map((e) => (e.id === id ? { ...e, read: true } : e)));
  };

  const deleteEnquiry = async (id) => {
    await api.delete(`/enquiries/${id}`);
    setEnquiries((es) => es.filter((e) => e.id !== id));
    toast.success("Enquiry deleted");
  };

  const openNew = () => { setForm(emptyJob); setEditing("new"); };
  const openEdit = (job) => { setForm({ ...emptyJob, ...job }); setEditing(job); };

  const saveJob = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post("/jobs", form);
        toast.success("Job posted");
      } else {
        await api.put(`/jobs/${editing.id}`, form);
        toast.success("Job updated");
      }
      setEditing(null);
      const { data } = await api.get("/jobs/all");
      setJobs(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteJob = async (id) => {
    await api.delete(`/jobs/${id}`);
    setJobs((js) => js.filter((j) => j.id !== id));
    toast.success("Job deleted");
  };

  const openNewInsight = () => { setInsightForm(emptyInsight); setEditingInsight("new"); };
  const openEditInsight = (a) => { setInsightForm({ ...emptyInsight, ...a }); setEditingInsight(a); };

  const uploadFeatured = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image is too large (max 5 MB)."); return; }
    const fd = new FormData();
    fd.append("file", file);
    setImgUploading(true);
    try {
      const { data } = await api.post("/uploads/image", fd);
      setInsightForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImgUploading(false);
    }
  };

  const saveInsight = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingInsight === "new") {
        await api.post("/insights", insightForm);
        toast.success(insightForm.published ? "Article published" : "Draft saved");
      } else {
        await api.put(`/insights/${editingInsight.id}`, insightForm);
        toast.success("Article updated");
      }
      setEditingInsight(null);
      const { data } = await api.get("/insights/all");
      setInsights(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteInsight = async (id) => {
    await api.delete(`/insights/${id}`);
    setInsights((as) => as.filter((a) => a.id !== id));
    toast.success("Article deleted");
  };

  const deleteApplication = async (id) => {
    await api.delete(`/applications/${id}`);
    setApplications((as) => as.filter((a) => a.id !== id));
    toast.success("Application deleted");
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8 || !/[a-zA-Z]/.test(pw.new_password) || !/[0-9]/.test(pw.new_password)) {
      toast.error("New password must be at least 8 characters with letters and numbers.");
      return;
    }
    if (pw.new_password !== pw.confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated. Use it next time you sign in.");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPwBusy(false);
    }
  };

  const unread = (enquiries || []).filter((e) => !e.read).length;
  const activeJobs = (jobs || []).filter((j) => j.active).length;
  const publishedCount = (insights || []).filter((a) => a.published).length;

  return (
    <div className="min-h-screen bg-[#F5F8FC]">
      <Seo title="Admin | Incremental Consulting" description="Incremental Consulting administration." />
      <header className="border-b border-[#0B1F3A]/10 bg-white">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <Logo />
          <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-sm font-semibold text-[#667085] hover:text-[#0B1F3A]">
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10" data-testid="admin-dashboard">
        <div className="grid gap-4 sm:grid-cols-4">
          <div className="rounded-xl border border-[#0B1F3A]/10 bg-white p-5" data-testid="admin-stat-enquiries">
            <p className="text-xs font-bold uppercase tracking-wider text-[#667085]">Total enquiries</p>
            <p className="mt-2 font-display text-3xl font-extrabold text-[#0B1F3A]">{enquiries?.length ?? "—"}</p>
          </div>
          <div className="rounded-xl border border-[#0B1F3A]/10 bg-white p-5" data-testid="admin-stat-unread">
            <p className="text-xs font-bold uppercase tracking-wider text-[#667085]">Unread</p>
            <p className="mt-2 font-display text-3xl font-extrabold text-[#A87B1F]">{enquiries ? unread : "—"}</p>
          </div>
          <div className="rounded-xl border border-[#0B1F3A]/10 bg-white p-5" data-testid="admin-stat-jobs">
            <p className="text-xs font-bold uppercase tracking-wider text-[#667085]">Active job posts</p>
            <p className="mt-2 font-display text-3xl font-extrabold text-[#0B1F3A]">{jobs ? activeJobs : "—"}</p>
          </div>
          <div className="rounded-xl border border-[#0B1F3A]/10 bg-white p-5" data-testid="admin-stat-applications">
            <p className="text-xs font-bold uppercase tracking-wider text-[#667085]">Applications</p>
            <p className="mt-2 font-display text-3xl font-extrabold text-[#0B1F3A]">{applications?.length ?? "—"}</p>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2" role="tablist">
          {[["enquiries", "Enquiries"], ["jobs", "Job Postings"], ["insights", "Insights"], ["applications", "Applications"], ["account", "Account"]].map(([key, label]) => (
            <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === key ? "bg-[#0B1F3A] text-white" : "bg-white text-[#667085] hover:text-[#0B1F3A]"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          <div className="mt-6 space-y-4" data-testid="admin-enquiries-list">
            {enquiries === null ? (
              <Loader2 size={18} className="animate-spin text-[#A87B1F]" />
            ) : enquiries.length === 0 ? (
              <p className="rounded-xl border border-dashed border-[#A87B1F]/30 bg-white p-8 text-sm text-[#667085]">No enquiries yet. New submissions from the contact form will appear here.</p>
            ) : (
              enquiries.map((enq) => (
                <article key={enq.id} className={`rounded-xl border bg-white p-6 ${enq.read ? "border-[#0B1F3A]/10" : "border-[#A87B1F]/40"}`} data-testid={`admin-enquiry-${enq.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="font-display text-base font-bold text-[#0B1F3A]">{enq.name}</h2>
                        {!enq.read && <span className="rounded-full bg-[#A87B1F]/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#A87B1F]">New</span>}
                      </div>
                      <p className="mt-1 text-[13px] text-[#667085]">{enq.email}{enq.phone ? ` · ${enq.phone}` : ""}{enq.company ? ` · ${enq.company}` : ""}</p>
                      <p className="mt-1 text-[13px] font-semibold text-[#A87B1F]">{enq.service}</p>
                    </div>
                    <div className="flex gap-2">
                      {!enq.read && (
                        <button onClick={() => markRead(enq.id)} data-testid={`admin-enquiry-read-${enq.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#0B1F3A]/15 px-3.5 py-1.5 text-xs font-semibold text-[#172033] hover:border-[#A87B1F] hover:text-[#A87B1F]">
                          <MailOpen size={13} /> Mark read
                        </button>
                      )}
                      <button onClick={() => deleteEnquiry(enq.id)} data-testid={`admin-enquiry-delete-${enq.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-3.5 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50">
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                  <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[#172033]/85">{enq.message}</p>
                  <p className="mt-3 text-[11px] text-[#98A2B3]">{new Date(enq.created_at).toLocaleString()}</p>
                </article>
              ))
            )}
          </div>
        )}

        {tab === "jobs" && (
          <div className="mt-6" data-testid="admin-jobs-section">
            <button onClick={openNew} data-testid="admin-job-new-button" className="inline-flex items-center gap-2 rounded-full bg-[#0B1F3A] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#A87B1F]">
              <Plus size={15} /> Post a job
            </button>

            {editing !== null && (
              <form onSubmit={saveJob} className="mt-5 rounded-xl border border-[#A87B1F]/30 bg-white p-6" data-testid="admin-job-form">
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-base font-bold text-[#0B1F3A]">{editing === "new" ? "New job posting" : "Edit job posting"}</h2>
                  <button type="button" onClick={() => setEditing(null)} aria-label="Close form" data-testid="admin-job-form-close"><X size={18} className="text-[#667085]" /></button>
                </div>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label htmlFor="jf-title" className="mb-1 block text-[13px] font-semibold">Job title *</label>
                    <input id="jf-title" required className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-job-input-title" placeholder="e.g. Senior Business Analyst" />
                  </div>
                  <div>
                    <label htmlFor="jf-dept" className="mb-1 block text-[13px] font-semibold">Department</label>
                    <input id="jf-dept" className={inputCls} value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="admin-job-input-department" placeholder="e.g. Consulting" />
                  </div>
                  <div>
                    <label htmlFor="jf-loc" className="mb-1 block text-[13px] font-semibold">Location</label>
                    <input id="jf-loc" className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="admin-job-input-location" placeholder="e.g. Fourways, Sandton / Hybrid" />
                  </div>
                  <div>
                    <label htmlFor="jf-type" className="mb-1 block text-[13px] font-semibold">Employment type</label>
                    <input id="jf-type" className={inputCls} value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })} data-testid="admin-job-input-type" placeholder="e.g. Full-time" />
                  </div>
                  <div className="flex items-end pb-1">
                    <label className="inline-flex items-center gap-2 text-sm font-semibold text-[#172033]">
                      <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="admin-job-input-active" className="h-4 w-4 accent-[#A87B1F]" />
                      Visible on Careers page
                    </label>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="jf-summary" className="mb-1 block text-[13px] font-semibold">Summary *</label>
                    <textarea id="jf-summary" required rows={2} className={inputCls} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="admin-job-input-summary" placeholder="One or two sentences shown on the job card" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="jf-desc" className="mb-1 block text-[13px] font-semibold">Full description</label>
                    <textarea id="jf-desc" rows={5} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-job-input-description" placeholder="Responsibilities, requirements, how to apply…" />
                  </div>
                </div>
                <button type="submit" disabled={busy} data-testid="admin-job-save-button" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#0B1F3A] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#A87B1F] disabled:opacity-60">
                  {busy && <Loader2 size={14} className="animate-spin" />}
                  {editing === "new" ? "Publish job" : "Save changes"}
                </button>
              </form>
            )}

            <div className="mt-6 space-y-4" data-testid="admin-jobs-list">
              {jobs === null ? (
                <Loader2 size={18} className="animate-spin text-[#A87B1F]" />
              ) : jobs.length === 0 ? (
                <p className="rounded-xl border border-dashed border-[#A87B1F]/30 bg-white p-8 text-sm text-[#667085]">No job postings yet. Post your first opening above.</p>
              ) : (
                jobs.map((job) => (
                  <article key={job.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#0B1F3A]/10 bg-white p-5" data-testid={`admin-job-${job.id}`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="font-display text-base font-bold text-[#0B1F3A]">{job.title}</h2>
                        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${job.active ? "bg-emerald-50 text-emerald-600" : "bg-[#0B1F3A]/5 text-[#667085]"}`}>
                          {job.active ? "Live" : "Hidden"}
                        </span>
                      </div>
                      <p className="mt-1 text-[13px] text-[#667085]">{[job.department, job.location, job.employment_type].filter(Boolean).join(" · ") || "—"}</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(job)} data-testid={`admin-job-edit-${job.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#0B1F3A]/15 px-3.5 py-1.5 text-xs font-semibold text-[#172033] hover:border-[#A87B1F] hover:text-[#A87B1F]">
                        <Pencil size={13} /> Edit
                      </button>
                      <button onClick={() => deleteJob(job.id)} data-testid={`admin-job-delete-${job.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-3.5 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50">
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </article>
                ))
              )}
            </div>
          </div>
        )}

        {tab === "insights" && (
          <div className="mt-6" data-testid="admin-insights-section">
            <button onClick={openNewInsight} data-testid="admin-insight-new-button" className="inline-flex items-center gap-2 rounded-full bg-[#0B1F3A] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#A87B1F]">
              <Plus size={15} /> Write an article
            </button>

            {editingInsight !== null && (
              <form onSubmit={saveInsight} className="mt-5 rounded-xl border border-[#A87B1F]/30 bg-white p-6" data-testid="admin-insight-form">
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-base font-bold text-[#0B1F3A]">{editingInsight === "new" ? "New article" : "Edit article"}</h2>
                  <button type="button" onClick={() => setEditingInsight(null)} aria-label="Close form" data-testid="admin-insight-form-close"><X size={18} className="text-[#667085]" /></button>
                </div>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label htmlFor="if-title" className="mb-1 block text-[13px] font-semibold">Title *</label>
                    <input id="if-title" required className={inputCls} value={insightForm.title} onChange={(e) => setInsightForm({ ...insightForm, title: e.target.value })} data-testid="admin-insight-input-title" placeholder="e.g. Five signs your processes need attention" />
                  </div>
                  <div>
                    <label htmlFor="if-category" className="mb-1 block text-[13px] font-semibold">Category *</label>
                    <select id="if-category" className={inputCls} value={insightForm.category} onChange={(e) => setInsightForm({ ...insightForm, category: e.target.value })} data-testid="admin-insight-input-category">
                      {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-[13px] font-semibold">Featured image</label>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => featuredRef.current?.click()} disabled={imgUploading} data-testid="admin-insight-upload-image" className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-[#0B1F3A]/15 px-4 py-2.5 text-[13px] font-semibold text-[#172033] hover:border-[#A87B1F] hover:text-[#A87B1F] disabled:opacity-60">
                        {imgUploading ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
                        {imgUploading ? "Uploading…" : "Upload from computer"}
                      </button>
                      <input ref={featuredRef} type="file" accept="image/*" className="hidden" onChange={uploadFeatured} data-testid="admin-insight-image-file-input" />
                      {insightForm.image_url && (
                        <img src={insightForm.image_url} alt="Featured preview" className="h-10 w-16 rounded-md object-cover" data-testid="admin-insight-image-preview" />
                      )}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="if-summary" className="mb-1 block text-[13px] font-semibold">Summary *</label>
                    <textarea id="if-summary" required rows={2} className={inputCls} value={insightForm.summary} onChange={(e) => setInsightForm({ ...insightForm, summary: e.target.value })} data-testid="admin-insight-input-summary" placeholder="One or two sentences shown on the article card" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-[13px] font-semibold">Article body *</label>
                    <RichTextEditor
                      key={editingInsight === "new" ? "new" : editingInsight.id}
                      value={insightForm.content}
                      onChange={(html) => setInsightForm({ ...insightForm, content: html })}
                      testId="admin-insight-editor"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="inline-flex items-center gap-2 text-sm font-semibold text-[#172033]">
                      <input type="checkbox" checked={insightForm.published} onChange={(e) => setInsightForm({ ...insightForm, published: e.target.checked })} data-testid="admin-insight-input-published" className="h-4 w-4 accent-[#A87B1F]" />
                      Publish (visible on the website)
                    </label>
                  </div>
                </div>
                <button type="submit" disabled={busy} data-testid="admin-insight-save-button" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#0B1F3A] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#A87B1F] disabled:opacity-60">
                  {busy && <Loader2 size={14} className="animate-spin" />}
                  {editingInsight === "new" ? (insightForm.published ? "Publish article" : "Save draft") : "Save changes"}
                </button>
              </form>
            )}

            <div className="mt-6 space-y-4" data-testid="admin-insights-list">
              {insights === null ? (
                <Loader2 size={18} className="animate-spin text-[#A87B1F]" />
              ) : insights.length === 0 ? (
                <p className="rounded-xl border border-dashed border-[#A87B1F]/30 bg-white p-8 text-sm text-[#667085]">No articles yet. Write your first insight above — drafts stay hidden until you publish them.</p>
              ) : (
                insights.map((a) => (
                  <article key={a.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#0B1F3A]/10 bg-white p-5" data-testid={`admin-insight-${a.id}`}>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="font-display text-base font-bold text-[#0B1F3A]">{a.title}</h2>
                        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${a.published ? "bg-emerald-50 text-emerald-600" : "bg-[#0B1F3A]/5 text-[#667085]"}`}>
                          {a.published ? "Published" : "Draft"}
                        </span>
                      </div>
                      <p className="mt-1 text-[13px] text-[#667085]">{a.category}{a.published_at ? ` · ${new Date(a.published_at).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" })}` : ""}</p>
                    </div>
                    <div className="flex gap-2">
                      {a.published && (
                        <a href={`/insights/${a.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-insight-view-${a.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#0B1F3A]/15 px-3.5 py-1.5 text-xs font-semibold text-[#172033] hover:border-[#A87B1F] hover:text-[#A87B1F]">
                          <Eye size={13} /> View
                        </a>
                      )}
                      <button onClick={() => openEditInsight(a)} data-testid={`admin-insight-edit-${a.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#0B1F3A]/15 px-3.5 py-1.5 text-xs font-semibold text-[#172033] hover:border-[#A87B1F] hover:text-[#A87B1F]">
                        <Pencil size={13} /> Edit
                      </button>
                      <button onClick={() => deleteInsight(a.id)} data-testid={`admin-insight-delete-${a.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-3.5 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50">
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </article>
                ))
              )}
            </div>
          </div>
        )}

        {tab === "applications" && (
          <div className="mt-6 space-y-4" data-testid="admin-applications-list">
            {applications === null ? (
              <Loader2 size={18} className="animate-spin text-[#A87B1F]" />
            ) : applications.length === 0 ? (
              <p className="rounded-xl border border-dashed border-[#A87B1F]/30 bg-white p-8 text-sm text-[#667085]">No applications yet. CVs submitted through the Careers page will appear here.</p>
            ) : (
              applications.map((a) => (
                <article key={a.id} className="rounded-xl border border-[#0B1F3A]/10 bg-white p-6" data-testid={`admin-application-${a.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="font-display text-base font-bold text-[#0B1F3A]">{a.name}</h2>
                      <p className="mt-1 text-[13px] text-[#667085]">{a.email}{a.phone ? ` · ${a.phone}` : ""}</p>
                      <p className="mt-1 text-[13px] font-semibold text-[#A87B1F]">{a.job_title}</p>
                    </div>
                    <div className="flex gap-2">
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${a.cv_path}`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid={`admin-application-cv-${a.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#0B1F3A]/15 px-3.5 py-1.5 text-xs font-semibold text-[#172033] hover:border-[#A87B1F] hover:text-[#A87B1F]"
                      >
                        <FileDown size={13} /> Download CV
                      </a>
                      <button onClick={() => deleteApplication(a.id)} data-testid={`admin-application-delete-${a.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-3.5 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50">
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                  {a.message && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[#172033]/85">{a.message}</p>}
                  <p className="mt-3 text-[11px] text-[#98A2B3]">{a.cv_filename} · {new Date(a.created_at).toLocaleString()}</p>
                </article>
              ))
            )}
          </div>
        )}

        {tab === "account" && (
          <div className="mt-6 max-w-md" data-testid="admin-account-section">
            <form onSubmit={changePassword} className="rounded-xl border border-[#0B1F3A]/10 bg-white p-7" data-testid="change-password-form">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#A87B1F]/10 text-[#A87B1F]">
                  <KeyRound size={18} />
                </span>
                <div>
                  <h2 className="font-display text-base font-bold text-[#0B1F3A]">Change password</h2>
                  <p className="text-[13px] text-[#667085]">Applies to the account you are signed in with.</p>
                </div>
              </div>
              <div className="mt-6 space-y-4">
                <div>
                  <label htmlFor="pw-current" className="mb-1 block text-[13px] font-semibold">Current password</label>
                  <input id="pw-current" type="password" required className={inputCls} value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="change-password-current" />
                </div>
                <div>
                  <label htmlFor="pw-new" className="mb-1 block text-[13px] font-semibold">New password</label>
                  <input id="pw-new" type="password" required className={inputCls} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="change-password-new" placeholder="At least 8 characters, letters and numbers" />
                </div>
                <div>
                  <label htmlFor="pw-confirm" className="mb-1 block text-[13px] font-semibold">Confirm new password</label>
                  <input id="pw-confirm" type="password" required className={inputCls} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="change-password-confirm" />
                </div>
              </div>
              <button type="submit" disabled={pwBusy} data-testid="change-password-submit" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0B1F3A] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#A87B1F] disabled:opacity-60">
                {pwBusy && <Loader2 size={14} className="animate-spin" />}
                Update password
              </button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
